import React from 'react';
import styled from 'styled-components';
import { Spin, Space } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { useIntl } from 'common/config/i18n';
import { ServicesPassengersConcernedByCommunicationResponse } from 'types';
import Card from 'components/molecules/card';
import { useTheme } from 'common/styling/theme';

export type Props = {
  data: number | undefined;
  status: 'loading' | 'error' | 'success' | undefined;
  isAlertMode?: boolean;
  onRefresh?: (callback?: (data?: ServicesPassengersConcernedByCommunicationResponse) => void) => void;
  height?: number;
  step: number;
  isNotAvailable?: boolean;
};

const Text = styled.p<{ center?: boolean; color?: string; weight?: string; size?: string }>`
  margin: 0;
  padding: 0;
  font-weight: ${(props) => (props.weight ? props.weight : 'bold')};
  color: ${(props) => (props.color ? props.color : props.theme ? props.theme.colors.primary.main : 'LightBlue')};
  ${(props) => (props.center ? 'text-align: center' : '')};
  ${(props) => (props.size ? `font-size: ${props.size}` : '')};
`;

const SyncIcon = styled.div<{ color: string }>`
  position: absolute;
  left: 8px;
  bottom: 0px;
  font-size: 1.5rem;
  color: ${(props) => props.color};
`;

const PassengersConcernedByCommunicationNotAvailable = ({ height }: { height?: number }) => {
  const { t } = useIntl();
  const { theme } = useTheme();

  return (
    <Card
      wrapperStyle={{
        backgroundColor: theme.colors.primary.light,
        alignItems: 'flex-start',
        margin: 0,
        maxWidth: '100%',
        height,
      }}
      full
    >
      <Space direction="vertical" align="center" style={{ width: '100%' }}>
        <Text center>{t('sendCommunication', 'passengerCount', 'headText')}</Text>
        <Text color={theme.colors.common.black} weight="normal" center>
          {t('sendCommunication', 'passengerCount', 'notAvailable')}
        </Text>
      </Space>
    </Card>
  );
};

const PassengersConcernedByCommunication = ({
  data,
  status,
  isAlertMode,
  onRefresh,
  height,
  step,
  isNotAvailable,
}: Props) => {
  const { t } = useIntl();
  const { theme } = useTheme();

  const onClick = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  if (isNotAvailable) {
    return <PassengersConcernedByCommunicationNotAvailable height={height} />;
  }

  return (
    <Card
      wrapperStyle={{
        backgroundColor: theme.colors.primary.light,
        alignItems: 'flex-start',
        margin: 0,
        maxWidth: '100%',
        border: isAlertMode ? `1px solid ${theme.colors.orange.main}` : 'none',
        height,
      }}
      full
    >
      <Space direction="vertical" align="center" style={{ width: '100%' }}>
        <Text size="1rem">{t('sendCommunication', 'passengerCount', 'headText')}</Text>
        {status === 'loading' ? (
          <Spin />
        ) : (
          <Text color={isAlertMode ? theme.colors.orange.main : theme.colors.primary.main} size="1.3rem">
            {!!data || data === 0 ? data : '-'}
          </Text>
        )}
        {step === 1 && (
          <SyncIcon color={isAlertMode ? theme.colors.orange.main : theme.colors.primary.main}>
            <SyncOutlined className="iconBlock" onClick={onClick} />
          </SyncIcon>
        )}
      </Space>
    </Card>
  );
};

export default PassengersConcernedByCommunication;
